import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import type {
  Card,
  ClassicCard,
  ControlMessage,
  FullScreenMessage,
  ModalMessage,
} from '@braze/web-sdk';
import hashString from './helpers/hashString';
import { handleBrazeInAppMessage } from '../utils/braze/inAppMessages';
import { useJssStore } from './jssStore';
import type { ContentCardsBlockCard, ParsedInAppMessage, HeroCarouselSlide } from '@/types';

type BrazeSDK = typeof import('@braze/web-sdk');

export const useBrazeStore = defineStore('braze', () => {
  const contentCards = ref<ClassicCard[] | null>(null);
  const feedLastUpdated = ref<number | null>(null);
  const isStoreSetupFailed = ref(false);
  let braze: BrazeSDK | null = null;

  const jssStore = useJssStore();
  const { brandName, enableContentCard, siteVirtualFolder, venueId, brazeSdkKey } =
    storeToRefs(jssStore);

  const getHeroCarouselContentCards = (): HeroCarouselSlide[] | undefined => {
    const pageTemplateName = jssStore.routeData?.templateName;

    const cards = contentCards.value?.filter(
      (card) =>
        card.extras?.['cc_component'] === 'HeroCarousel' &&
        card.extras?.['cc_page_template'] === pageTemplateName
    );

    if (!cards?.length) return;

    const controlCards = cards.filter((card) => card.extras?.['cc_control'] === 'yes');

    if (controlCards?.length) {
      logControlCardsDisplay(controlCards);
    }

    return parseHeroCarouselCard(
      cards.filter((card) => card.extras?.['cc_control'] === 'no'),
      siteVirtualFolder.value || '/'
    );
  };

  const contentCardsBlockCards = computed(
    () =>
      contentCards.value
        ?.filter(
          (card) =>
            card.extras?.['cc_component'] === 'ContentCardsBlock' &&
            card.extras?.['cc_page_template'] === jssStore.routeData?.templateName &&
            !Number.isNaN(Number.parseInt(card.extras?.['cc_order']))
        )
        .sort((a, b) =>
          a.extras['cc_order'].localeCompare(b.extras['cc_order'], undefined, { numeric: true })
        )
  );

  const contentCardsBlockControlCards = computed(
    () => contentCardsBlockCards.value?.filter((card) => card.extras?.['cc_control'] === 'yes')
  );

  const getContentCardsBlockCards = (controlCards = false): ContentCardsBlockCard[] | undefined => {
    const cards = contentCardsBlockCards.value
      ?.map((c, index) => ({ ...c, index }))
      ?.filter((card) => card.extras?.['cc_control'] === (controlCards ? 'yes' : 'no'));

    return parseContentCardsBlockCard(cards, siteVirtualFolder.value || '/');
  };

  const logControlCardsDisplay = (cards: Card[]) => {
    braze?.logContentCardImpressions(cards);
  };

  const logContentCardDisplay = (cardId: string) => {
    braze?.logContentCardImpressions(
      contentCards.value?.filter((card) => card.id === cardId) ?? []
    );
  };

  const logContentCardClick = (cardId: string) => {
    braze?.logContentCardClick(contentCards.value?.find((card) => card.id === cardId));
  };

  const onContentCardsUpdate = ({
    cards,
    lastUpdated,
  }: {
    cards: Card[];
    lastUpdated: Date | null;
  }) => {
    if (!lastUpdated || feedLastUpdated.value === lastUpdated.getTime()) return;

    const validContentCards = cards.filter(
      (card) =>
        card.extras['cc_brand'] === brandName.value && card.extras['cc_venueid'] === venueId.value
    );

    feedLastUpdated.value = lastUpdated.getTime();
    contentCards.value = validContentCards as ClassicCard[];
  };

  const onInAppMessageUpdate = (
    inAppMessage: FullScreenMessage | ModalMessage | ControlMessage
  ) => {
    if (inAppMessage.extras?.['cc_control'] === 'yes') {
      braze?.logInAppMessageImpression(inAppMessage);
      handleBrazeInAppMessage(
        parseInAppMessage(inAppMessage as FullScreenMessage | ModalMessage),
        true
      );
    } else {
      braze?.showInAppMessage(inAppMessage);

      // The build-in control messages are handled by the showInAppMessage method automatically
      if (!inAppMessage.isControl)
        handleBrazeInAppMessage(
          parseInAppMessage(inAppMessage as FullScreenMessage | ModalMessage)
        );
    }
  };

  const updateContentCardFeed = () => {
    braze?.requestContentCardsRefresh();

    setTimeout(() => updateContentCardFeed(), 30000);
  };

  /**
   * Sets up the user in Braze with a hashed external ID and email.
   * @param email - The user's email address.
   */
  const setupUser = async (email = '') => {
    if (braze) {
      const emailTrimmed = email.trim().toLowerCase();
      const externalId = await hashString(emailTrimmed);
      if (externalId) {
        braze.changeUser(externalId);

        const user = braze.getUser();
        user?.setEmail(emailTrimmed);

        braze.requestImmediateDataFlush();
      }
    }
  };

  const setupSDK = () => {
    braze.initialize(brazeSdkKey.value, {
      baseUrl: 'sdk.fra-02.braze.eu',
      doNotLoadFontAwesome: true,
      allowUserSuppliedJavascript: true, // added for BrazeBanners POC
    });
    braze.subscribeToContentCardsUpdates(onContentCardsUpdate);
    braze.subscribeToInAppMessage(onInAppMessageUpdate);

    braze.openSession();

    isStoreSetupFailed.value = false;

    updateContentCardFeed();
  };

  const initializeStore = async () => {
    if (braze) return;
    if (!enableContentCard.value || !brazeSdkKey.value) {
      isStoreSetupFailed.value = true;
      return;
    }

    try {
      braze = await import(
        /* webpackChunkName: "braze-sdk" */
        /* webpackPrefetch: true */
        /* webpackExports: ["initialize", "openSession", "subscribeToContentCardsUpdates",
          "requestContentCardsRefresh", "logContentCardImpressions", "logContentCardClick",
          "logCustomEvent", "isInitialized", "changeUser", "getUser",
          "subscribeToBannersUpdates", "requestBannersRefresh", "insertBanner", "getBanner",
          "subscribeToInAppMessage", "showInAppMessage", "requestImmediateDataFlush", "logInAppMessageImpression"] */
        '@braze/web-sdk'
      );
    } catch {
      console.error('Import of Braze module failed');
      isStoreSetupFailed.value = true;
      return;
    }

    window.braze = {
      logCustomEvent: braze.logCustomEvent,
      isInitialized: braze.isInitialized,
      requestContentCardsRefresh: braze.requestContentCardsRefresh,
    };

    if (!window?.Cookiebot?.consent?.marketing) {
      window.addEventListener('CookiebotOnAccept', setupSDK);
      isStoreSetupFailed.value = true;
      return;
    }

    setupSDK();
  };

  return {
    contentCards,
    contentCardsBlockCards,
    contentCardsBlockControlCards,
    feedLastUpdated,
    isStoreSetupFailed,
    initializeStore,
    setupUser,
    getHeroCarouselContentCards,
    getContentCardsBlockCards,
    logContentCardDisplay,
    logControlCardsDisplay,
    logContentCardClick,
  };
});

const parseHeroCarouselCard = (
  cards: ClassicCard[] | undefined,
  baseUrl: string
): HeroCarouselSlide[] | undefined =>
  cards?.map((card) => ({
    componentName: 'HeroCarouselItem',
    contentCard: true,
    contentCardId: card.id,
    fields: {
      subtitle: { value: card?.description },
      title: { value: card?.title },
      imageDesktop: { value: { src: card?.imageUrl, alt: card.extras?.['cc_img_alt_text'] ?? '' } },
      showTransparentLayer: { value: true },
      link: {
        value: {
          href: card?.url && (baseUrl + card.url).replace('//', '/'),
          text: card?.linkText,
          target: '_self',
          linktype: 'internal',
        },
      },
    },
  }));

const parseContentCardsBlockCard = (
  cards: (ClassicCard & { index: number })[] | undefined,
  baseUrl: string
): ContentCardsBlockCard[] | undefined =>
  cards?.map((card) => ({
    id: card.id,
    title: card.title,
    description: card.description,
    image: { src: card.imageUrl, alt: card.extras?.['cc_img_alt_text'] ?? '' },
    link: {
      href: card.url && (baseUrl + card.url).replace('//', '/'),
      text: card.linkText,
      target: '_self',
      linktype: 'internal',
    },
    analyticsData: {
      promotion_id: card.title,
      promotion_name: card.title,
      creative_name: card.description,
      card_id: card.extras?.['cc_card_id'],
      card_name: card.extras?.['cc_card_name'],
      card_variant_id: card.extras?.['cc_card_variant_id'],
      card_variant_name: card.extras?.['cc_card_variant_name'],
      creative_slot: card.index + 1,
    },
  }));

const parseInAppMessage = (inAppMessage: FullScreenMessage | ModalMessage): ParsedInAppMessage => ({
  buttons: inAppMessage.buttons,
  analyticsData: {
    promotion_id: inAppMessage.header,
    promotion_name: inAppMessage.header,
    creative_name: inAppMessage.message,
    card_id: inAppMessage.extras?.['cc_card_id'],
    card_name: inAppMessage.extras?.['cc_card_name'],
    card_variant_id: inAppMessage.extras?.['cc_card_variant_id'],
    card_variant_name: inAppMessage.extras?.['cc_card_variant_name'],
    creative_slot: 1,
  },
});
